<template>
  <div class="row">
    <div class="col-md-4">
      <doughnut-chart
        v-if="loaded"
        :chart-data="responseDoughnutData"
        :options="responseDoughnutOptions"
      />
    </div>
    <div class="col-md-4">
      <line-chart
        v-if="loaded"
        :chart-data="lineData"
        :options="lineOptions"
      />
    </div>
    <div class="col-md-4">
      <doughnut-chart
        v-if="loaded"
        :chart-data="methodDoughnutData"
        :options="methodDoughnutOptions"
      />
    </div>
  </div>
</template>

<script>
import DoughnutChart from './../../shared/Charts/DoughnutChart.js'
import LineChart from '../../shared/Charts/LineChart'
import axios from 'axios'
import { defaultErrorMessage } from '@/helpers'

export default {
  name: 'ApiLogAnalytics',

  components: { LineChart, DoughnutChart },

  props: {
    url: { required: true },
  },

  data: () => ({
    loaded: false,
    logs: null,
    searchFilter: null,

    responseCodes: [
      { code: 200, color: '#6bbd5b', name: 'OK' },
      { code: 400, color: '#f7c72a', name: 'Bad Request' },
      { code: 401, color: '#f7c72a', name: 'Unauthorized' },
      { code: 404, color: '#f7c72a', name: 'Not Found' },
      { code: 403, color: '#f78121', name: 'Forbidden' },
      { code: 405, color: '#f78121', name: 'Method not allowed' },
      { code: 500, color: '#e27070', name: 'Internal Server Error' },
    ],

    responseMethods: [
      { method: 'GET', color: '#6bbd5b' },
      { method: 'POST', color: '#248fb2' },
      { method: 'PUT', color: '#73708b' },
      { method: 'DELETE', color: '#e27070' },
    ],
  }),

  computed: {
    filteredLogs () {
      if (this.searchFilter === null || this.searchFilter === undefined) { return this.logs }

      return Object.values(this.logs).filter(log => {
        return this.filterLog(log, this.searchFilter[0])
      })
    },

    responseDoughnutData () {
      return {
        labels: this.responseCodes.map(code => code.code + ' ' + code.name),
        datasets: [
          {
            backgroundColor: this.responseCodes.map(code => code.color),
            data: this.responseCodes.map(code => this.propCount('response_code', code.code)),
          },

        ],
      }
    },

    responseDoughnutOptions () {
      return {
        title: {
          display: true,
          text: Lang.get('api.response_code'),
        },

        legend: {
          position: 'bottom',
        },
      }
    },

    lineData () {
      var data = this.getLineData()
      return {
        labels: data.map(point => point.x),
        datasets: [
          {
            lineTension: 0.2,
            label: 'Requests',
            backgroundColor: '#6bbd5b',
            borderColor: '#6bbd5b',
            data: data.map(point => point.y),
            fill: false,
          },

        ],
      }
    },

    lineOptions () {
      return {
        title: {
          display: true,
          text: Lang.get('api.logs_title'),
        },

        legend: {
          position: 'bottom',
        },

        tooltips: {
          mode: 'index',
          intersect: false,
        },

        scales: {
          xAxes: [{
            type: 'time',
            time: {
              unit: 'day',
              displayFormats: {
                day: 'MMM D',
              },
            },

          }],
          yAxes: [{
            type: 'linear',
            ticks: {
              min: 0,
            },
          }],
        },
      }
    },

    methodDoughnutData () {
      return {
        labels: this.responseMethods.map(method => method.method),
        datasets: [
          {
            backgroundColor: this.responseMethods.map(method => method.color),
            data: this.responseMethods.map(method => this.propCount('method', method.method)),
          },

        ],
      }
    },

    methodDoughnutOptions () {
      return {
        title: {
          display: true,
          text: Lang.get('api.method'),
        },

        legend: {
          position: 'bottom',
        },
      }
    },

  },

  async mounted () {
    this.loaded = false
    window.setInterval(this.updateFilters, 1000)

    axios.get(this.url).then(({ data }) => {
      this.logs = data.data.data.sort((a, b) => a.raw_time - b.raw_time)
      this.loaded = true
    }).catch(error => defaultErrorMessage(error))
  },

  methods: {
    getLineData () {
      if (this.filteredLogs.length === 0) {
        return []
      }
      var keyVal = []

      var firstHourTimestamp = this.roundTimestamps(this.filteredLogs[0].raw_time, 3600000) // 3600000 = 1 hour
      var lastHourTimestamp = this.roundTimestamps(this.filteredLogs[this.filteredLogs.length - 1].raw_time, 3600000) // 3600000 = 1 hour

      for (var hourTimestamp = firstHourTimestamp; hourTimestamp < lastHourTimestamp; hourTimestamp += 3600000) { // 3600000 = 1 hour (add one hour)
        var height = Object.values(this.filteredLogs).filter(log => {
          return log.raw_time > (hourTimestamp - 1800000) && log.raw_time <= (hourTimestamp + 1800000) // 1800000 = 30 minutes (30 minutes before or after the whole hour)
        }).length

        keyVal.push({ x: new Date(hourTimestamp), y: height })
      }

      return keyVal
    },

    roundTimestamps (timestamp, miliseconds) {
      var prev = timestamp - (timestamp % miliseconds)
      var next = prev + miliseconds

      if ((timestamp - prev) < (next - timestamp)) {
        return prev
      } else {
        return next
      }
    },

    propCount (property, value) {
      return Object.values(this.filteredLogs).filter(log => {
        return log[property] === value
      }).length
    },

    updateFilters () {
      if (this.loaded) {
        this.searchFilter = location.queryString.search
      }
    },

    filterLog (log, searchString) {
      var searchArray = searchString.split(' ')
      for (var property in log) {
        for (var search of searchArray) {
          if (('' + log[property]).toLowerCase().includes(search.toLowerCase()) && property !== 'raw_time') {
            return true
          }
        }
      }
      return false
    },
  },
}
</script>

<style scoped>
</style>
